import React from 'react';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import { useNavigate } from 'react-router-dom';

import { VatixError } from 'components/Error/types';

import Grid, { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { formatMoment } from 'utils/formatters/timezone';
import { FULL_DATETIME_COLUMN_MIN_WIDTH, FULL_DATETIME_FORMAT } from 'core/constants';

import { TemplateListResponse } from 'utils/api/modules/workflows/workflows.types';

import routes from 'core/routes';

import { env } from 'config';

import ActiveColumn from '../ActiveColumn';
import ActionCell from '../FormTemplatesActionCell';
import ActiveFilterOperator from '../ActiveFilterOperator';

export const basicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    nativeFilter: 'query',
  },
  {
    field: 'creator',
    headerName: 'Creator',
    nativeFilter: 'creator',
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    filterOperators: defaultOperators.user(),
    valueGetter: (value: unknown) => ({ value }),
  },
  {
    field: 'lastUpdate',
    nativeFilter: 'last_update',
    headerName: 'Last modified',
    filterOperators: defaultOperators.date_time(),
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    valueFormatter: (value: string) => formatMoment(value, FULL_DATETIME_FORMAT),
    renderCell: ({ value }: { value: string }) => (
      <span data-testid="form-templates-last-modified">{formatMoment(value, FULL_DATETIME_FORMAT)}</span>
    ),
  },
  {
    field: 'isActive',
    headerName: 'Status',
    renderCell: ({ row, colDef }: { row: TemplateListResponse; colDef: ColumnDefinition }) => (
      <ActiveColumn row={row} actions={colDef.actions} />
    ),
    filterOperators: ActiveFilterOperator(),
    nativeFilter: 'is_active',
    sortingField: 'is_active',
  },
  {
    maxWidth: 50,
    headerName: '',
    type: 'actions',
    field: 'actions',
    ariaLabel: 'actions',
    renderCell: ({ row }: { row: TemplateListResponse }) => <ActionCell rowData={row} />,
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const FormTemplatesList: React.FunctionComponent<{ onError: (error: VatixError) => void }> = ({
  onError,
}): React.ReactElement => {
  const navigate = useNavigate();
  const onRowClick: GridEventListener<'rowClick'> = (template: GridRowParams): void => {
    navigate(reverse(routes.dashboard.audits.templates.details, { templateId: template.row.uuid }));
  };

  return (
    <Grid
      onRowClick={onRowClick}
      basicColumns={basicColumns}
      entity="forms templates"
      customNoRowsText="No form templates have been created yet"
      onError={onError}
      dataURL={`${env.REACT_APP_WORKFLOWS_API_URL}templates/`}
      sortBy={{ field: 'lastUpdate', sort: 'desc' }}
      sortingMapping={{
        lastUpdate: 'last_update',
      }}
      disableColumnSelector
    />
  );
};

export default FormTemplatesList;
